import React, { useMemo, useState } from 'react';
import Modal from '@/components/elements/Modal/Modal';
import Icon from '@/components/elements/Icon/Icon';
import CloseIcon from '@/assets/icons/close.svg';
import Button from '@/components/elements/Button/Button';
import { Input } from '@/components/elements/Input/Input';
import { Select } from '@/components/elements/Dropdown/Select';
import type { DropdownOption } from '@/components/elements/Dropdown';

export type CurrentPlanFormData = {
    monthlyPremium: string;
    deductible: string;
    maxOutOfPocket: string;
    carrier: string;
    planName: string;
    metalLevel: string;
};

export interface CompareCurrentPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddToCompare: (data: CurrentPlanFormData) => void;
    disableAdd?: boolean;
}

const metalOptions: DropdownOption[] = [
    { label: 'Catastrophic', value: 'Catastrophic' },
    { label: 'Bronze', value: 'Bronze' },
    { label: 'Silver', value: 'Silver' },
    { label: 'Gold', value: 'Gold' },
    { label: 'Platinum', value: 'Platinum' },
];

export const CompareCurrentPlanModal: React.FC<CompareCurrentPlanModalProps> = ({
    isOpen,
    onClose,
    onAddToCompare,
    disableAdd = false,
}) => {
    const [monthlyPremium, setMonthlyPremium] = useState('');
    const [deductible, setDeductible] = useState('');
    const [maxOutOfPocket, setMaxOutOfPocket] = useState('');
    const [carrier, setCarrier] = useState('');
    const [planName, setPlanName] = useState('');
    const [metalLevel, setMetalLevel] = useState<string>('');
    const [error, setError] = useState<string | null>(null);

    const formatMoneyInput = (raw: string) => {
        if (!raw) return '';

        let intPart = '';
        let fracPart = '';
        let sawDot = false;

        for (const ch of raw) {
            if (ch >= '0' && ch <= '9') {
                if (sawDot) fracPart += ch;
                else intPart += ch;
            } else if (ch === '.' && !sawDot) {
                sawDot = true;
            }
        }

        if (!intPart && !sawDot) return '';

        const intWithCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        if (!sawDot) return intWithCommas;
        return `${intWithCommas}.${fracPart}`;
    };

    const canSubmit = useMemo(() => {
        if (disableAdd) return false;
        return (
            monthlyPremium.trim() &&
            deductible.trim() &&
            maxOutOfPocket.trim() &&
            carrier.trim() &&
            planName.trim() &&
            metalLevel.trim()
        );
    }, [carrier, deductible, disableAdd, maxOutOfPocket, metalLevel, monthlyPremium, planName]);

    const handleClose = () => {
        setError(null);
        onClose();
    };

    const handleSubmit = () => {
        if (disableAdd) {
            setError('Remove one plan from Compare Plans to add your current plan.');
            return;
        }

        if (!canSubmit) {
            setError('Please fill in all fields.');
            return;
        }

        setError(null);
        onAddToCompare({
            monthlyPremium,
            deductible,
            maxOutOfPocket,
            carrier,
            planName,
            metalLevel,
        });
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            size="auto"
            showCloseButton={false}
            modalBoxClass="w-[95vw] max-w-[750px] max-h-[85vh] overflow-y-auto p-4 md:p-6"
        >
            <div className="flex items-start justify-between gap-4">
                <h2 className="text-2xl md:text-[32px] leading-tight font-semibold text-gray-900">
                    Enter Your Current Plan Details
                </h2>
                <button
                    type="button"
                    onClick={handleClose}
                    className="flex h-12 w-12 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Close modal"
                >
                    <Icon src={CloseIcon} className="p-2" />
                </button>
            </div>

            <div className="mt-5 flex flex-col gap-4">
                {error && <div className="text-sm font-medium text-red-600">{error}</div>}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <Input
                        id="current-monthly-premium"
                        label="Monthly Premium"
                        placeholder="0.00"
                        required
                        prefix='$'
                        value={monthlyPremium}
                        onChange={(e) => setMonthlyPremium(formatMoneyInput(e.target.value))}
                        onBlur={(e) => setMonthlyPremium(formatMoneyInput(e.target.value))}
                    />
                    <Input
                        id="current-deductible"
                        label="Deductible"
                        placeholder="0.00"
                        prefix='$'
                        required
                        value={deductible}
                        onChange={(e) => setDeductible(formatMoneyInput(e.target.value))}
                        onBlur={(e) => setDeductible(formatMoneyInput(e.target.value))}
                    />
                    <Input
                        id="current-moop"
                        label="Max-out-of-pocket"
                        placeholder="0.00"
                        prefix='$'
                        value={maxOutOfPocket}
                        onChange={(e) => setMaxOutOfPocket(formatMoneyInput(e.target.value))}
                        onBlur={(e) => setMaxOutOfPocket(formatMoneyInput(e.target.value))}
                    />
                </div>

                <Input
                    id="current-carrier"
                    label="Carrier"
                    placeholder="Type here"
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value)}
                />

                <Input
                    id="current-plan-name"
                    label="Plan Name"
                    placeholder="Type here"
                    value={planName}
                    onChange={(e) => setPlanName(e.target.value)}
                />

                <Select
                    id="current-metal-level"
                    label="Metal Level"
                    options={metalOptions}
                    value={metalLevel}
                    onChange={(value) => setMetalLevel(String(value))}
                    placeholder="Select"
                    position="top"
                />

                <div className="flex items-center justify-end gap-3 pt-2">
                    <Button type="button" variant="white" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button type="button" variant="primary" disabled={!canSubmit} onClick={handleSubmit}>
                        Add to Compare
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

